import { useEffect, useRef, MutableRefObject } from 'react';
import { closeComplianceSession } from '@features/admin/api/complianceServices';
import { incrementCumulativeTime, updateLessonProgress } from '@features/courses/api/progressServices';

interface UseSessionUnloadBeaconProps {
  userId: string | undefined;
  courseId: string;
  sessionId: string | null;
  isActive: boolean;
  isOnBreak: boolean;
  sessionTimeRef: MutableRefObject<number>;
  lessonTimeRef: MutableRefObject<number>;
  currentLessonIdRef: MutableRefObject<string | null>;
  lastSyncedTimeRef: MutableRefObject<number>;
  beforeUnloadHandlerRef: MutableRefObject<((event: BeforeUnloadEvent) => void) | null>;
}

export const useSessionUnloadBeacon = ({
  userId,
  courseId,
  sessionId,
  isActive,
  isOnBreak,
  sessionTimeRef,
  lessonTimeRef,
  currentLessonIdRef,
  lastSyncedTimeRef,
  beforeUnloadHandlerRef
}: UseSessionUnloadBeaconProps) => {
  // Sync the unload handler definition when key inputs change
  useEffect(() => {
    // Clean up previous listener if it exists
    if (beforeUnloadHandlerRef.current) {
      window.removeEventListener('beforeunload', beforeUnloadHandlerRef.current);
      beforeUnloadHandlerRef.current = null;
    }

    if (userId && sessionId) {
      beforeUnloadHandlerRef.current = (_event: BeforeUnloadEvent) => {
        if (!isOnBreak) {
          try {
            navigator.sendBeacon(
              '/api/sessions/close',
              JSON.stringify({
                userId,
                sessionId,
                closureType: 'page_unload',
                duration: sessionTimeRef.current
              })
            );
          } catch (error) {
            console.error('[SessionUnloadBeacon] Failed to send unload beacon:', error);
          }
        } else {
          console.warn('[SessionUnloadBeacon] Bypassing beforeunload session closure beacon because break is active.');
        }
      };

      window.addEventListener('beforeunload', beforeUnloadHandlerRef.current);
    }

    return () => {
      if (beforeUnloadHandlerRef.current) {
        window.removeEventListener('beforeunload', beforeUnloadHandlerRef.current);
        beforeUnloadHandlerRef.current = null;
      }
    };
  }, [userId, sessionId, isOnBreak, sessionTimeRef, beforeUnloadHandlerRef]);

  // 1. Create a ref dictionary to hold fresh state values without re-running effects
  const unmountRefs = useRef({
    userId,
    courseId,
    sessionId,
    isActive,
    isOnBreak,
    sessionTimeRef,
    lastSyncedTimeRef,
    currentLessonIdRef,
    lessonTimeRef
  });

  // 2. Refresh the ref dictionary on every render loop to capture the latest values
  useEffect(() => {
    unmountRefs.current = {
      userId,
      courseId,
      sessionId,
      isActive,
      isOnBreak,
      sessionTimeRef,
      lastSyncedTimeRef,
      currentLessonIdRef,
      lessonTimeRef
    };
  });

  // 3. Handle final cleanup EXACTLY ONCE on actual component unmount
  useEffect(() => {
    return () => {
      // Read the exact fresh state values at the precise millisecond of unmounting
      const {
        userId: uId,
        courseId: cId,
        sessionId: sId,
        isActive: active,
        isOnBreak: onBreak,
        sessionTimeRef: sTimeRef,
        lastSyncedTimeRef: lsTimeRef,
        currentLessonIdRef: clIdRef,
        lessonTimeRef: lTimeRef
      } = unmountRefs.current;

      const currentSessionTime = sTimeRef.current;
      const deltaSeconds = currentSessionTime - lsTimeRef.current;

      if (active && sId && uId && !onBreak) {
        console.warn(`[SessionUnloadBeacon] Component unmounting with active session ${sId}. Closing...`);

        // Remove unload handler
        if (beforeUnloadHandlerRef.current) {
          window.removeEventListener('beforeunload', beforeUnloadHandlerRef.current);
        }

        // Attempt to close session in Firestore
        const closingData = {
          duration: currentSessionTime,
          closureType: 'component_unmount',
          lastUpdated: new Date().toISOString()
        };

        closeComplianceSession(uId, sId, closingData).catch(err => {
          console.error('[SessionUnloadBeacon] Failed to close session on unmount:', err);
        });

        // Save progress for the final active lesson upon component unmount
        const activeLessonId = clIdRef.current;
        const activeLessonTime = lTimeRef.current;
        if (activeLessonId && activeLessonTime > 0 && cId) {
          updateLessonProgress(uId, cId, activeLessonId, { timeSpent: activeLessonTime }).catch(err => {
            console.error('[SessionUnloadBeacon] Failed to save active lesson progress on unmount:', err);
          });
        }

        // Sync final cumulative time
        if (deltaSeconds > 0 && cId) {
          incrementCumulativeTime(uId, cId, deltaSeconds).catch(err => {
            console.error('[SessionUnloadBeacon] Failed to sync cumulative time on unmount:', err);
          });
        }
      } else if (onBreak) {
        console.warn(`[SessionUnloadBeacon] Component unmounting with active break on session ${sId}. Skipping session close.`);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beforeUnloadHandlerRef]);
};
